import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  defaultLlmSections,
  defaultLlmState,
  defaultLlmSubjectState,
  getLlmCache,
} from './utils';
import {
  ILlmSubjectSummaryFieldsFragment,
  ILlmSummarizeSectionsInput,
  LlmSubjectKind,
} from '../../../utils/apollo/graphqlSchema';

export default createSlice({
  name: 'llmSlice',
  initialState: defaultLlmState,
  reducers: {
    llmPopulateSubjectSummary: (
      draft,
      action: PayloadAction<{
        subjectKind: LlmSubjectKind;
        subjectId: string;
        summary: ILlmSubjectSummaryFieldsFragment;
      }>,
    ) => {
      const { subjectKind, subjectId, summary } = action.payload;
      const cache = getLlmCache(draft, subjectKind);
      const current = cache[subjectId];
      const { __typename, ...sections } = summary.sections;
      cache[subjectId] = {
        llmSummary: summary,
        sections,
        loading: false,
        cachedRawData: current?.cachedRawData,
      };
    },
    llmClearSubjectSummary: (
      draft,
      action: PayloadAction<{
        subjectKind: LlmSubjectKind;
        subjectId: string;
      }>,
    ) => {
      const { subjectKind, subjectId } = action.payload;
      const cache = getLlmCache(draft, subjectKind);
      const current = cache[subjectId];
      if (!!current) {
        current.llmSummary = null;
      }
    },
    llmSetLoading: (
      draft,
      action: PayloadAction<{
        subjectKind: LlmSubjectKind;
        subjectId: string;
        loading: boolean;
      }>,
    ) => {
      const { subjectId, subjectKind, loading } = action.payload;
      const cache = getLlmCache(draft, subjectKind);
      const current = cache[subjectId] ?? defaultLlmSubjectState;
      cache[subjectId] = {
        ...current,
        loading,
      };
    },
    llmToggleSummarySection: (
      draft,
      action: PayloadAction<{
        subjectKind: LlmSubjectKind;
        subjectId: string;
        section: keyof ILlmSummarizeSectionsInput;
      }>,
    ) => {
      const { subjectId, subjectKind, section } = action.payload;
      const cache = getLlmCache(draft, subjectKind);
      const current = cache[subjectId] ?? defaultLlmSubjectState;
      const newSections = {
        ...current.sections,
      };
      newSections[section] = !newSections[section];
      cache[subjectId] = {
        ...current,
        sections: newSections,
      };
    },
    llmSetCachedRawData: (
      draft,
      action: PayloadAction<{
        subjectKind: LlmSubjectKind;
        subjectId: string;
        rawData: string | null;
      }>,
    ) => {
      const { subjectKind, subjectId, rawData } = action.payload;
      const cache = getLlmCache(draft, subjectKind);
      const current = cache[subjectId];
      cache[subjectId] = {
        llmSummary: current?.llmSummary ?? null,
        sections: current?.sections ?? defaultLlmSections,
        loading: current?.loading ?? false,
        cachedRawData: rawData,
      };
    },
  },
});
